/**
 * FileSystemService
 *
 * Clean, direct File System Access API service for browser-based file operations.
 * Replaces the window.electron abstraction with direct API utilities.
 */

import type { ProjectManifest, RecentProject } from '../../core/types/project.types';
import {
  saveProjectHandle,
  getProjectHandle,
  removeProjectHandle,
  getAllProjectHandles,
  requestPermission,
  verifyPermission,
} from '../utils/handle-storage';

// ============================================================================
// Constants
// ============================================================================

const RECENT_PROJECTS_KEY = 'pixijs-editor-recent-projects';
const PROJECT_MANIFEST_FILE = '.project.json';

// ============================================================================
// Core State
// ============================================================================

let projectRootHandle: FileSystemDirectoryHandle | null = null;

// ============================================================================
// Handle Management
// ============================================================================

/**
 * Set the active project root directory handle
 */
export function setProjectRoot(handle: FileSystemDirectoryHandle | null): void {
  projectRootHandle = handle;
  if (process.env.NODE_ENV === 'development') {
    console.log('[FileSystemService] Project root set:', handle?.name || 'null');
  }
}

/**
 * Get the active project root directory handle
 */
export function getProjectRoot(): FileSystemDirectoryHandle | null {
  return projectRootHandle;
}

/**
 * Check if a project root is currently set
 */
export function hasProjectRoot(): boolean {
  return projectRootHandle !== null;
}

// ============================================================================
// Path Utilities
// ============================================================================

/**
 * Parse a path string into segments, normalizing separators
 */
function parsePathSegments(path: string): string[] {
  return path
    .replace(/\\/g, '/')
    .split('/')
    .filter(segment => segment.length > 0 && segment !== '.');
}

/**
 * Get the parent directory path
 */
function getParentPath(path: string): string {
  const segments = parsePathSegments(path);
  segments.pop();
  return segments.join('/');
}

/**
 * Get the file/directory name from a path
 */
function getBaseName(path: string): string {
  const segments = parsePathSegments(path);
  return segments[segments.length - 1] || '';
}

// ============================================================================
// Handle Navigation (Internal)
// ============================================================================

/**
 * Navigate to a directory handle from the project root
 * Returns null if the path doesn't exist and create is false
 */
async function navigateToDirectory(
  relativePath: string,
  options: { create?: boolean } = {}
): Promise<FileSystemDirectoryHandle | null> {
  if (!projectRootHandle) {
    throw new Error('No project root set. Call setProjectRoot first.');
  }

  const segments = parsePathSegments(relativePath);
  if (segments.length === 0) {
    return projectRootHandle;
  }

  let currentHandle: FileSystemDirectoryHandle = projectRootHandle;

  for (const segment of segments) {
    try {
      currentHandle = await currentHandle.getDirectoryHandle(segment, {
        create: options.create,
      });
    } catch (error) {
      if (options.create) {
        throw new Error(`Failed to create directory "${segment}": ${(error as Error).message}`);
      }
      return null;
    }
  }

  return currentHandle;
}

/**
 * Navigate to a file handle from the project root
 * Returns null if the file doesn't exist and create is false
 */
async function navigateToFile(
  relativePath: string,
  options: { create?: boolean } = {}
): Promise<FileSystemFileHandle | null> {
  if (!projectRootHandle) {
    throw new Error('No project root set. Call setProjectRoot first.');
  }

  const segments = parsePathSegments(relativePath);
  if (segments.length === 0) {
    throw new Error('Invalid file path: empty path');
  }

  const fileName = segments.pop()!;
  let currentHandle: FileSystemDirectoryHandle = projectRootHandle;

  // Navigate to parent directory
  for (const segment of segments) {
    try {
      currentHandle = await currentHandle.getDirectoryHandle(segment, {
        create: options.create,
      });
    } catch (error) {
      if (options.create) {
        throw new Error(`Failed to create directory "${segment}": ${(error as Error).message}`);
      }
      return null;
    }
  }

  // Get the file handle
  try {
    return await currentHandle.getFileHandle(fileName, { create: options.create });
  } catch {
    return null;
  }
}

// ============================================================================
// File Operations
// ============================================================================

// Maximum file size for text files (5MB)
const MAX_TEXT_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Read the contents of a file as text
 * @param relativePath - Path relative to project root
 * @returns File contents as string
 * @throws Error if file is too large (>5MB)
 */
export async function readFile(relativePath: string): Promise<string> {
  const fileHandle = await navigateToFile(relativePath);
  if (!fileHandle) {
    throw new Error(`File not found: ${relativePath}`);
  }

  const file = await fileHandle.getFile();

  // Check file size to prevent OOM on large files
  if (file.size > MAX_TEXT_FILE_SIZE) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(2);
    throw new Error(
      `File too large: ${relativePath} (${sizeMB}MB). Maximum size for text files is 5MB.`
    );
  }

  return await file.text();
}

/**
 * Read the contents of a file as an ArrayBuffer
 * @param relativePath - Path relative to project root
 * @returns File contents as ArrayBuffer
 */
export async function readFileAsArrayBuffer(relativePath: string): Promise<ArrayBuffer> {
  const fileHandle = await navigateToFile(relativePath);
  if (!fileHandle) {
    throw new Error(`File not found: ${relativePath}`);
  }

  const file = await fileHandle.getFile();
  return await file.arrayBuffer();
}

/**
 * Write content to a file, creating it and parent directories if they don't exist
 * @param relativePath - Path relative to project root
 * @param content - Content to write
 */
export async function writeFile(relativePath: string, content: string): Promise<void> {
  // Ensure parent directories exist
  const parentPath = getParentPath(relativePath);
  if (parentPath) {
    await createDirectory(parentPath, true);
  }

  const fileHandle = await navigateToFile(relativePath, { create: true });
  if (!fileHandle) {
    throw new Error(`Could not create file: ${relativePath}`);
  }

  const writable = await fileHandle.createWritable();
  try {
    await writable.write(content);
  } finally {
    await writable.close();
  }
}

/**
 * Write binary content to a file
 * @param relativePath - Path relative to project root
 * @param content - Binary content to write
 */
export async function writeFileAsArrayBuffer(
  relativePath: string,
  content: ArrayBuffer | Uint8Array
): Promise<void> {
  // Ensure parent directories exist
  const parentPath = getParentPath(relativePath);
  if (parentPath) {
    await createDirectory(parentPath, true);
  }

  const fileHandle = await navigateToFile(relativePath, { create: true });
  if (!fileHandle) {
    throw new Error(`Could not create file: ${relativePath}`);
  }

  const writable = await fileHandle.createWritable();
  try {
    await writable.write(content);
  } finally {
    await writable.close();
  }
}

/**
 * Check if a file exists at the given path
 * @param relativePath - Path relative to project root
 */
export async function fileExists(relativePath: string): Promise<boolean> {
  try {
    const fileHandle = await navigateToFile(relativePath);
    return fileHandle !== null;
  } catch {
    return false;
  }
}

/**
 * Delete a file
 * @param relativePath - Path relative to project root
 */
export async function deleteFile(relativePath: string): Promise<void> {
  if (!projectRootHandle) {
    throw new Error('No project root set. Call setProjectRoot first.');
  }

  const parentPath = getParentPath(relativePath);
  const fileName = getBaseName(relativePath);

  if (!fileName) {
    throw new Error('Invalid file path');
  }

  let parentHandle: FileSystemDirectoryHandle;
  if (parentPath) {
    const handle = await navigateToDirectory(parentPath);
    if (!handle) {
      throw new Error(`Parent directory not found: ${parentPath}`);
    }
    parentHandle = handle;
  } else {
    parentHandle = projectRootHandle;
  }

  await parentHandle.removeEntry(fileName);
}

/**
 * Move (or rename) a file from one path to another.
 * Reads from source, writes to destination, then deletes the source.
 * @param srcPath - Current relative path
 * @param destPath - New relative path
 */
export async function moveFile(srcPath: string, destPath: string): Promise<void> {
  const content = await readFileAsArrayBuffer(srcPath);
  await writeFileAsArrayBuffer(destPath, content);
  await deleteFile(srcPath);
}

/**
 * Get file metadata
 * @param relativePath - Path relative to project root
 */
export async function getFileInfo(
  relativePath: string
): Promise<{ size: number; lastModified: number; type: string }> {
  const fileHandle = await navigateToFile(relativePath);
  if (!fileHandle) {
    throw new Error(`File not found: ${relativePath}`);
  }

  const file = await fileHandle.getFile();
  return {
    size: file.size,
    lastModified: file.lastModified,
    type: file.type,
  };
}

// ============================================================================
// Directory Operations
// ============================================================================

/**
 * Create a directory (and parent directories if recursive is true)
 * @param relativePath - Path relative to project root
 * @param recursive - Create parent directories if they don't exist
 */
export async function createDirectory(relativePath: string, recursive = false): Promise<void> {
  if (!projectRootHandle) {
    throw new Error('No project root set. Call setProjectRoot first.');
  }

  const segments = parsePathSegments(relativePath);
  if (segments.length === 0) {
    return; // Root directory already exists
  }

  let currentHandle = projectRootHandle;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    try {
      currentHandle = await currentHandle.getDirectoryHandle(segment, { create: recursive || i === segments.length - 1 });
    } catch (error) {
      if (!recursive && i < segments.length - 1) {
        throw new Error(`Parent directory does not exist: ${segments.slice(0, i + 1).join('/')}`);
      }
      throw new Error(`Failed to create directory "${segment}": ${(error as Error).message}`);
    }
  }
}

/**
 * Read the contents of a directory
 * @param relativePath - Path relative to project root (empty string for root)
 * @returns Array of entries with name and isDirectory flag
 */
export async function readDirectory(
  relativePath: string
): Promise<Array<{ name: string; isDirectory: boolean }>> {
  const dirHandle = await navigateToDirectory(relativePath);
  if (!dirHandle) {
    throw new Error(`Directory not found: ${relativePath || '(root)'}`);
  }

  const entries: Array<{ name: string; isDirectory: boolean }> = [];
  for await (const entry of dirHandle.values()) {
    entries.push({
      name: entry.name,
      isDirectory: entry.kind === 'directory',
    });
  }

  return entries;
}

/**
 * List directory contents with full FileSystemHandle information
 * @param relativePath - Path relative to project root
 * @returns Array of FileSystemHandle entries (files and directories)
 */
export async function listDirectory(
  relativePath: string
): Promise<Array<{ name: string; kind: 'file' | 'directory' }>> {
  if (!projectRootHandle) {
    throw new Error('No project root set');
  }

  const dirHandle = await navigateToDirectory(relativePath);
  if (!dirHandle) {
    return [];
  }

  const entries: Array<{ name: string; kind: 'file' | 'directory' }> = [];
  for await (const entry of dirHandle.values()) {
    entries.push({
      name: entry.name,
      kind: entry.kind,
    });
  }
  return entries;
}

/**
 * Check if a directory exists at the given path
 * @param relativePath - Path relative to project root
 */
export async function directoryExists(relativePath: string): Promise<boolean> {
  try {
    const dirHandle = await navigateToDirectory(relativePath);
    return dirHandle !== null;
  } catch {
    return false;
  }
}

/**
 * Delete a directory (must be empty unless recursive is true)
 * @param relativePath - Path relative to project root
 * @param recursive - Delete contents recursively
 */
export async function deleteDirectory(relativePath: string, recursive = false): Promise<void> {
  if (!projectRootHandle) {
    throw new Error('No project root set. Call setProjectRoot first.');
  }

  const parentPath = getParentPath(relativePath);
  const dirName = getBaseName(relativePath);

  if (!dirName) {
    throw new Error('Cannot delete project root');
  }

  let parentHandle: FileSystemDirectoryHandle;
  if (parentPath) {
    const handle = await navigateToDirectory(parentPath);
    if (!handle) {
      throw new Error(`Parent directory not found: ${parentPath}`);
    }
    parentHandle = handle;
  } else {
    parentHandle = projectRootHandle;
  }

  await parentHandle.removeEntry(dirName, { recursive });
}

// ============================================================================
// Dialog Operations
// ============================================================================

/**
 * Check if running in OPFS mode (for Playwright/automation).
 * Activated by URL parameter: ?fs=opfs
 */
function isOPFSMode(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('fs') === 'opfs';
  } catch {
    return false;
  }
}

/**
 * Get a directory handle from the Origin Private File System.
 * Used as a fallback when native file picker is unavailable (e.g., Playwright).
 * @param folderName - Name of the folder to create/get in OPFS (from ?folder= param or default)
 */
async function getOPFSDirectory(folderName?: string): Promise<FileSystemDirectoryHandle> {
  const params = new URLSearchParams(window.location.search);
  const name = folderName || params.get('folder') || 'playground';
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(name, { create: true });
}

/**
 * Show a directory picker dialog.
 * When ?fs=opfs URL param is set, uses Origin Private File System instead of native dialog.
 * This allows the app to work in Playwright and other automation environments.
 * @returns Selected directory handle or null if cancelled
 */
export async function showDirectoryPicker(
  options?: DirectoryPickerOptions
): Promise<FileSystemDirectoryHandle | null> {
  // OPFS mode: bypass native dialog for Playwright/automation
  if (isOPFSMode()) {
    try {
      return await getOPFSDirectory();
    } catch (error) {
      console.error('[OPFS] Failed to get directory:', error);
      return null;
    }
  }

  try {
    const handle = await window.showDirectoryPicker({
      mode: 'readwrite',
      startIn: 'documents',
      ...options,
    });
    return handle;
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      return null; // User cancelled
    }
    throw error;
  }
}

/**
 * Show a file picker dialog for opening files
 * @returns Selected file handles or null if cancelled
 */
export async function showFilePicker(
  options?: OpenFilePickerOptions
): Promise<FileSystemFileHandle[] | null> {
  try {
    const handles = await window.showOpenFilePicker({
      multiple: false,
      ...options,
    });
    return handles;
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      return null; // User cancelled
    }
    throw error;
  }
}

/**
 * Show a file picker dialog for saving files
 * @returns File handle or null if cancelled
 */
export async function showSaveFilePicker(
  options?: SaveFilePickerOptions
): Promise<FileSystemFileHandle | null> {
  try {
    const handle = await window.showSaveFilePicker(options);
    return handle;
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      return null; // User cancelled
    }
    throw error;
  }
}

// ============================================================================
// Project Operations
// ============================================================================

/**
 * Generate a unique project ID from a directory handle
 */
function generateProjectId(handle: FileSystemDirectoryHandle): string {
  return `project-${handle.name}-${Date.now()}`;
}

/**
 * Validate a project directory by checking for a valid manifest
 * @param handle - Directory handle to validate
 * @returns Validation result with manifest if valid
 */
export async function validateProject(
  handle: FileSystemDirectoryHandle
): Promise<{ valid: boolean; manifest?: ProjectManifest; error?: string }> {
  try {
    const fileHandle = await handle.getFileHandle(PROJECT_MANIFEST_FILE);
    const file = await fileHandle.getFile();
    const content = await file.text();
    const manifest: ProjectManifest = JSON.parse(content);

    // Basic validation of required fields
    if (!manifest.name || !manifest.version || !manifest.engine) {
      return {
        valid: false,
        error: 'Invalid manifest: missing required fields (name, version, or engine)',
      };
    }

    return { valid: true, manifest };
  } catch (error) {
    if ((error as Error).name === 'NotFoundError') {
      return {
        valid: false,
        error: `Not a valid project: ${PROJECT_MANIFEST_FILE} not found`,
      };
    }
    return {
      valid: false,
      error: `Failed to read project manifest: ${(error as Error).message}`,
    };
  }
}

/**
 * Load the project manifest from the current project root
 * @returns Project manifest
 */
export async function loadProjectManifest(): Promise<ProjectManifest> {
  const content = await readFile(PROJECT_MANIFEST_FILE);
  return JSON.parse(content);
}

/**
 * Save the project manifest to the current project root
 * @param manifest - Project manifest to save
 */
export async function saveProjectManifest(manifest: ProjectManifest): Promise<void> {
  // Update modification timestamp
  const updatedManifest: ProjectManifest = {
    ...manifest,
    modifiedAt: new Date().toISOString(),
  };
  await writeFile(PROJECT_MANIFEST_FILE, JSON.stringify(updatedManifest, null, 2));
}

// ============================================================================
// Recent Projects Management
// ============================================================================

interface StoredRecentProject {
  id: string;
  name: string;
  directoryName: string;
  lastOpened: string;
}

function getStoredRecentProjects(): StoredRecentProject[] {
  try {
    const stored = localStorage.getItem(RECENT_PROJECTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setStoredRecentProjects(projects: StoredRecentProject[]): void {
  localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(projects));
}

/**
 * Get the list of recent projects with validated handles
 * @returns Array of recent projects
 */
export async function getRecentProjects(): Promise<RecentProject[]> {
  const stored = getStoredRecentProjects();
  const handles = await getAllProjectHandles();
  const validProjects: RecentProject[] = [];

  for (const project of stored) {
    const handle = handles.get(project.id);
    if (handle) {
      // Check if we still have permission
      const hasPermission = await verifyPermission(handle);

      // Verify the project is still valid
      const validation = await validateProject(handle);
      if (validation.valid) {
        validProjects.push({
          name: project.name,
          path: project.id, // Use ID as path for handle-based projects
          directoryName: project.directoryName || handle.name,
          size: 0, // Size calculation would require iterating all files
          lastOpened: project.lastOpened,
        });
      }
    }
  }

  return validProjects;
}

/**
 * Add a project to the recent projects list
 * @param project - Project info to add
 * @param handle - Directory handle for the project
 */
export async function addToRecentProjects(
  project: RecentProject,
  handle: FileSystemDirectoryHandle
): Promise<void> {
  // Generate ID if using path as ID
  const projectId = project.path.startsWith('project-')
    ? project.path
    : generateProjectId(handle);

  // Save handle to IndexedDB
  await saveProjectHandle(projectId, handle);

  // Update localStorage list
  const stored = getStoredRecentProjects();
  const updated: StoredRecentProject[] = [
    {
      id: projectId,
      name: project.name,
      directoryName: handle.name,
      lastOpened: new Date().toISOString(),
    },
    ...stored.filter(p => p.id !== projectId),
  ].slice(0, 10); // Keep max 10 recent projects

  setStoredRecentProjects(updated);
}

/**
 * Remove a project from the recent projects list
 * @param projectId - Project ID to remove
 */
export async function removeFromRecentProjects(projectId: string): Promise<void> {
  // Remove from IndexedDB
  await removeProjectHandle(projectId);

  // Remove from localStorage
  const stored = getStoredRecentProjects();
  setStoredRecentProjects(stored.filter(p => p.id !== projectId));
}

/**
 * Clear all recent projects
 */
export async function clearRecentProjects(): Promise<void> {
  // Get all handles and remove them
  const handles = await getAllProjectHandles();
  for (const id of handles.keys()) {
    await removeProjectHandle(id);
  }

  // Clear localStorage
  setStoredRecentProjects([]);
}

/**
 * Get a stored project handle by ID and request permission if needed
 * @param projectId - Project ID
 * @returns Directory handle or null if not found
 */
export async function getStoredProjectHandle(
  projectId: string
): Promise<FileSystemDirectoryHandle | null> {
  const handle = await getProjectHandle(projectId);
  if (!handle) {
    return null;
  }

  // Request permission if needed
  const hasPermission = await requestPermission(handle);
  if (!hasPermission) {
    return null;
  }

  return handle;
}

// ============================================================================
// TypeScript Declarations for File System Access API
// ============================================================================

interface FilePickerAcceptType {
  description?: string;
  accept: Record<string, string | string[]>;
}

interface DirectoryPickerOptions {
  id?: string;
  mode?: 'read' | 'readwrite';
  startIn?: 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos' | FileSystemHandle;
}

interface OpenFilePickerOptions {
  multiple?: boolean;
  excludeAcceptAllOption?: boolean;
  types?: FilePickerAcceptType[];
  startIn?: 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos' | FileSystemHandle;
}

interface SaveFilePickerOptions {
  excludeAcceptAllOption?: boolean;
  suggestedName?: string;
  types?: FilePickerAcceptType[];
  startIn?: 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos' | FileSystemHandle;
}

declare global {
  interface Window {
    showDirectoryPicker(options?: DirectoryPickerOptions): Promise<FileSystemDirectoryHandle>;
    showOpenFilePicker(options?: OpenFilePickerOptions): Promise<FileSystemFileHandle[]>;
    showSaveFilePicker(options?: SaveFilePickerOptions): Promise<FileSystemFileHandle>;
  }

  // Extend FileSystemDirectoryHandle with values() method
  interface FileSystemDirectoryHandle {
    values(): AsyncIterableIterator<FileSystemHandle>;
  }
}

// Export types for consumers
export type { DirectoryPickerOptions, OpenFilePickerOptions, SaveFilePickerOptions, FilePickerAcceptType };
